import { BRAND_STEP_SLUGS } from '@adthlete/common/utils/collaborationState'
import type { Collaboration, Milestone, Submission } from '@/redux/marketerApi'

/**
 * The collaboration workspace's stages, in order, for any kind of deal:
 *
 *   Agreement → Compliance → the deal's work steps → Live & codes
 *
 * Content deals' work steps are Creative concept and Content review; gifting, appearance and
 * licensing deals have their own (product shipped, check-in, draft…). Each stage says whose move
 * it is, so the workspace can open on whatever needs the brand.
 */
export type StageState = 'done' | 'needs_you' | 'with_athlete' | 'upcoming' | 'closed'
export type StageKind = 'agreement' | 'compliance' | 'concept' | 'content' | 'step' | 'live'

export type Stage = {
  key: string
  kind: StageKind
  label: string
  state: StageState
  /** One short line under the label: "Approved", "Awaiting your review", "Due Oct 20" */
  status: string
  /** When it was finished, or when it's due */
  date: string | null
  milestone?: Milestone
}

const LIVE_SLUGS = ['collaboration_live', 'content_live']
const CONTENT_SLUGS = ['submit_video', 'post_content', 'create_draft']
const STEP_LABEL: Record<string, string> = {
  submit_creative_concept: 'Creative concept',
  submit_video: 'Content review',
  post_content: 'Content review',
  create_draft: 'Content review',
  product_shipped: 'Ship product',
  confirm_received: 'Product received',
  prepare_appearance: 'Appearance details',
  check_in: 'Check-in',
  brand_confirms: 'Confirm appearance',
  brand_approves: 'Final approval',
}

const isDone = (m: Milestone) => m.status === 'approved' || m.status === 'completed'
export const latestSubmission = (m: Milestone | undefined): Submission | undefined =>
  m ? [...m.submissions].sort((a, b) => b.version - a.version)[0] : undefined
const short = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null

const eventDate = (c: Collaboration, event: string) =>
  c.timeline.find((t) => (t.metadata as any)?.event === event)?.createdAt ?? null

export function buildStages(c: Collaboration): Stage[] {
  const agreed = c.status === 'accepted'
  const stages: Stage[] = []

  // Agreement
  stages.push({
    key: 'agreement',
    kind: 'agreement',
    label: 'Agreement',
    ...(agreed
      ? { state: 'done', status: 'Offer accepted', date: eventDate(c, 'offer_accepted') }
      : c.status === 'pending_brand'
        ? { state: 'needs_you', status: 'Your decision', date: null }
        : c.status === 'offered'
          ? { state: 'with_athlete', status: 'Waiting on athlete', date: null }
          : {
              state: 'closed',
              status:
                c.status === 'expired'
                  ? 'Expired'
                  : c.status === 'withdrawn'
                    ? 'Withdrawn'
                    : 'Declined',
              date: c.status === 'withdrawn' ? eventDate(c, 'offer_withdrawn') : null,
            }),
  } as Stage)

  // Compliance (NIL Go clearance on deals of $600 or more)
  const nil = c.compliance
  stages.push({
    key: 'compliance',
    kind: 'compliance',
    label: 'Compliance',
    ...(!agreed
      ? { state: 'upcoming', status: 'After agreement', date: null }
      : !nil?.required
        ? { state: 'done', status: 'Not required', date: null }
        : nil.state === 'cleared'
          ? { state: 'done', status: 'Approved', date: nil.clearedAt }
          : nil.state === 'overdue'
            ? { state: 'with_athlete', status: 'Overdue', date: nil.deadline }
            : { state: 'with_athlete', status: 'In progress', date: nil.deadline }),
  } as Stage)

  // The deal's own work steps
  const work = c.milestones
    .filter(
      (m) =>
        m.slug !== 'agree_on_terms' &&
        m.slug !== 'license_content' &&
        !LIVE_SLUGS.includes(m.slug ?? ''),
    )
    .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
  const current = work.find((m) => !isDone(m))
  for (const m of work) {
    const slug = m.slug ?? ''
    const latest = latestSubmission(m)
    let state: StageState = 'upcoming'
    let status = 'Not started'
    if (isDone(m)) {
      state = 'done'
      status = m.requiresApproval ? 'Approved' : 'Done'
    } else if (!agreed) {
      state = 'upcoming'
    } else if (latest?.status === 'pending_review') {
      state = 'needs_you'
      status = 'Awaiting your review'
    } else if (m === current && BRAND_STEP_SLUGS.includes(slug)) {
      state = 'needs_you'
      status = 'Your step'
    } else if (m.status === 'revision_requested' || latest?.status === 'revision_requested') {
      state = 'with_athlete'
      status = 'Revising'
    } else if (m === current) {
      state = 'with_athlete'
      status = 'In progress'
    }
    stages.push({
      key: m.id,
      kind:
        slug === 'submit_creative_concept'
          ? 'concept'
          : CONTENT_SLUGS.includes(slug)
            ? 'content'
            : 'step',
      label: STEP_LABEL[slug] ?? m.name,
      state,
      status,
      date: isDone(m) ? m.completedAt : m.dueDate,
      milestone: m,
    })
  }

  // Live & codes: the post going up, and the platform codes for running it as an ad
  const live = c.milestones.find((m) => LIVE_SLUGS.includes(m.slug ?? ''))
  const license = c.milestones.find((m) => m.slug === 'license_content')
  const posted = (live && isDone(live)) || (c.postLinks?.length ?? 0) > 0
  const codesDone = !license || isDone(license)
  const workDone = work.every(isDone)
  stages.push({
    key: 'live',
    kind: 'live',
    label: 'Live & codes',
    ...(posted && codesDone
      ? { state: 'done', status: 'Live', date: live?.completedAt ?? null }
      : posted
        ? { state: 'with_athlete', status: 'Codes pending', date: null }
        : agreed && workDone
          ? { state: 'with_athlete', status: 'Posting', date: live?.dueDate ?? null }
          : { state: 'upcoming', status: 'Upcoming', date: live?.dueDate ?? c.contentDueDate }),
    milestone: live,
  } as Stage)

  return stages
}

/** Where the workspace opens: whatever needs the brand, else what's happening now */
export function activeStageKey(stages: Stage[]) {
  return (
    stages.find((s) => s.state === 'needs_you') ??
    stages.find((s) => s.state === 'with_athlete') ??
    stages.find((s) => s.state === 'upcoming') ??
    stages[stages.length - 1]
  ).key
}

export const stageDate = (s: Stage) =>
  s.date ? (s.state === 'done' ? short(s.date) : `Due ${short(s.date)}`) : null

/** The pill colour for a stage's state */
export const stateTone = (s: Stage['state']) =>
  s === 'done' ? 'green' : s === 'needs_you' ? 'blue' : s === 'with_athlete' ? 'amber' : 'grey'
